import { openStateAt } from '@theme/store-hours';

const COPY_FEEDBACK_MS = 1500;

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-store-detail-hero] [data-store-copy]');
  if (!button) return;
  event.preventDefault();
  copyToClipboard(button);
});

document.querySelectorAll('[data-store-detail-hero]').forEach((hero) => {
  hero.querySelectorAll('[data-store-open-status]').forEach((badge) => {
    refreshOpenStatusFor(hero, badge, new Date());
  });
});

// Opening hours ship collapsed (mobile + no-JS default); expand them by default
// on desktop. Track the breakpoint live, but stop overriding once the user has
// toggled the accordion themselves. Keyboard activation of <summary> also
// dispatches a click, so the click listener covers both input methods; we avoid
// the async `toggle` event since it can't distinguish our programmatic open from
// a real interaction.
document.querySelectorAll('[data-store-detail-hero] .store-detail-hero__hours').forEach((details) => {
  const desktop = window.matchMedia('(min-width: 750px)');
  let userToggled = false;
  details.querySelector('summary')?.addEventListener('click', () => {
    userToggled = true;
  }, { once: true });
  const applyDefault = () => {
    if (!userToggled) details.open = desktop.matches;
  };
  applyDefault();
  desktop.addEventListener('change', applyDefault);
});

async function copyToClipboard(button) {
  const value = button.dataset.copyValue || '';
  if (!value) return;
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(value);
    } else {
      copyViaTextarea(value);
    }
  } catch {
    copyViaTextarea(value);
  }
  const feedback = button.querySelector('[data-store-copy-feedback]');
  if (!feedback) return;
  feedback.hidden = false;
  setTimeout(() => {
    feedback.hidden = true;
  }, COPY_FEEDBACK_MS);
}

function copyViaTextarea(value) {
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    document.execCommand('copy');
  } catch {
    // give up silently
  }
  document.body.removeChild(textarea);
}

function refreshOpenStatusFor(hero, badge, now) {
  const text = badge.querySelector('.store-card__open-text');
  if (!text) return;

  const json = badge.dataset.openingHoursJson;
  if (!json) {
    badge.dataset.openState = 'unknown';
    return;
  }

  let parsed;
  try {
    parsed = JSON.parse(json);
  } catch {
    badge.dataset.openState = 'unknown';
    return;
  }
  if (!parsed || typeof parsed !== 'object') {
    badge.dataset.openState = 'unknown';
    return;
  }

  const state = openStateAt(parsed, now);
  if (state.open) {
    badge.dataset.openState = 'open';
    text.innerHTML = formatTimeTemplate(hero.dataset.templateClosesAt, state.closesAt);
    return;
  }

  badge.dataset.openState = 'closed';
  if (!state.nextOpen) {
    text.textContent = hero.dataset.labelClosed || 'Closed';
    return;
  }
  text.innerHTML = formatTimeTemplate(hero.dataset.templateOpensAt, state.nextOpen.open);
}

function formatTimeTemplate(template, time) {
  if (!template) return escapeHtml(time);
  return template
    .replace('{{ time }}', escapeHtml(time))
    .replace('{time}', escapeHtml(time));
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/`/g, '&#96;');
}

function wireScrollCarousel({ scroller, controls, prev, next, itemSelector }) {
  if (!scroller || !controls || !prev || !next) return;

  const step = () => {
    const firstItem = scroller.querySelector(itemSelector);
    if (!firstItem) return scroller.clientWidth;
    const gap = parseFloat(getComputedStyle(scroller).columnGap || '0') || 0;
    return firstItem.getBoundingClientRect().width + gap;
  };

  const sync = () => {
    const max = scroller.scrollWidth - scroller.clientWidth;
    const overflowing = max > 1;
    controls.hidden = !overflowing;
    if (!overflowing) return;
    const atStart = scroller.scrollLeft <= 1;
    const atEnd = scroller.scrollLeft >= max - 1;
    prev.setAttribute('aria-disabled', String(atStart));
    next.setAttribute('aria-disabled', String(atEnd));
    prev.disabled = atStart;
    next.disabled = atEnd;
  };

  prev.addEventListener('click', () => scroller.scrollBy({ left: -step(), behavior: 'smooth' }));
  next.addEventListener('click', () => scroller.scrollBy({ left: step(), behavior: 'smooth' }));

  let frame = 0;
  scroller.addEventListener('scroll', () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(sync);
  });
  new ResizeObserver(sync).observe(scroller);
  sync();
}

/**
 * Hides the "Leggi di più" trigger on cards whose description is not actually
 * clamped (VIS-911). Measured rather than guessed from the character count:
 * the clamp depends on the font and on the card width, which changes at the
 * 750px breakpoint, so a Liquid-side threshold would be wrong on one of them.
 *
 * @param {Element} root - The services carousel root.
 * @param {Element | null} scroller - The scrolling list, observed for resizes.
 */
function wireServiceReadMore(root, scroller) {
  const descriptions = root.querySelectorAll('[data-service-description]');
  if (!descriptions.length || !scroller) return;

  const sync = () => {
    descriptions.forEach((text) => {
      const button = text.parentElement?.querySelector('[data-service-more]');
      // +1 absorbs the sub-pixel rounding of a fractional line-height.
      if (button) button.hidden = text.scrollHeight <= text.clientHeight + 1;
    });
  };

  new ResizeObserver(sync).observe(scroller);
  sync();
}

document.querySelectorAll('[data-store-services-carousel]').forEach((root) => {
  wireScrollCarousel({
    scroller: root.querySelector('[data-services-scroller]'),
    controls: root.querySelector('[data-services-controls]'),
    prev: root.querySelector('[data-services-prev]'),
    next: root.querySelector('[data-services-next]'),
    itemSelector: '.store-detail__services-item',
  });

  wireServiceReadMore(root, root.querySelector('[data-services-scroller]'));
});

document.querySelectorAll('[data-store-gallery-carousel]').forEach((root) => {
  wireScrollCarousel({
    scroller: root.querySelector('[data-gallery-scroller]'),
    controls: root.querySelector('[data-gallery-controls]'),
    prev: root.querySelector('[data-gallery-prev]'),
    next: root.querySelector('[data-gallery-next]'),
    itemSelector: '.store-detail__gallery-item',
  });
});
